import { isDefined } from "@omni/utils/helper.utils";
import { debounce } from "lodash-es";
import { useEffect, useRef, useState } from "react";

import {
    SearchItem,
    SearchItemType
} from "@/components/Store/StoreSearchInput/storeSearchInput.types";
import { useWoosmapServices } from "@/utils/hooks/useWoosmapServices.hook";

import { geocodeMapper } from "../mappers/geocode.mapper";
import { storeFeatureMapper } from "../mappers/storeFeature.mapper";
import { findGeocode } from "../queries/findGeocode.query";
import { findStoresByBounds } from "../queries/findStoresByBounds.query";
import { findStoresByLatLng } from "../queries/findStoresByLatLng.query";
import { findStoresByStore } from "../queries/findStoresByStore.query";
import { findStoresByTags } from "../queries/findStoresByTags.query";
import { LOC_ZOOM } from "../storeSearch.config";
import { findStoreUrl } from "../storeSearch.helpers";
import { StoreSearchStore, StoreUrl } from "../storeSearch.types";
import { useInfoWindow } from "./useInfoWindow.hook";
import { useMap } from "./useMap.hook";
import { usePreferredStoreMarker } from "./usePreferredStoreMarker.hook";
import { useStoresOverlay } from "./useStoresOverlay.hook";

const parseSearchParamAsFloat = (
    searchParam: string,
    searchParams: URLSearchParams
) => {
    const parsedSearchParam = parseFloat(searchParams.get(searchParam) ?? "");

    return Number.isNaN(parsedSearchParam) ? undefined : parsedSearchParam;
};

type useWoosmapProps = {
    mapElementRef: React.RefObject<HTMLDivElement | null>;
    storeUrls: Array<StoreUrl>;
};

const useWoosmap = ({ mapElementRef, storeUrls }: useWoosmapProps) => {
    const mapFlyingRef = useRef<boolean>(false);

    const [stores, setStores] = useState<StoreSearchStore[] | undefined>(
        undefined
    );

    const [searchItem, setSearchItem] = useState<SearchItem | undefined>(
        undefined
    );

    const [selectedStore, setSelectedStore] = useState<
        StoreSearchStore | undefined
    >(undefined);

    const { LocalitiesService, StoresService, DistanceService } =
        useWoosmapServices();

    const map = useMap(mapElementRef);

    usePreferredStoreMarker(mapElementRef, storeUrls);

    const { setMapQuery, setMapSelection, clearMapSelection } =
        useStoresOverlay(mapElementRef);

    const { open: openInfoWindow, close: closeInfoWindow } = useInfoWindow(
        mapElementRef,
        storeUrls,
        () => {
            setSelectedStore(undefined);
            clearMapSelection();
        }
    );

    const flyTo = (options: woosmap.map.FlyToOptions) => {
        mapFlyingRef.current = true;
        map?.flyTo(options);
    };

    const resetSearchItem = () => {
        setSearchItem(undefined);
    };

    const handleStoreSelected = (
        storeGeoJSON: woosmap.map.stores.StoreResponse
    ) => {
        openInfoWindow(storeGeoJSON);
        setSelectedStore({
            ...storeGeoJSON,
            url: findStoreUrl(
                storeUrls,
                storeGeoJSON.properties.user_properties.storeId
            )
        });
    };

    const handleOnBoundsChanged = debounce(() => {
        if (!isDefined(map)) return;

        const urlSearchParams = new URLSearchParams(location.search);
        const tags = urlSearchParams.getAll("features");

        const bounds = map.getBounds();

        updateStoresByMapBounds(tags, bounds);
    }, 500);

    const initEvents = () => {
        if (!isDefined(map)) return;

        map.addListener("store_selected", handleStoreSelected);

        map.addListener("click", () => {
            closeInfoWindow();
            setSelectedStore(undefined);
        });

        map.addListener("bounds_changed", () => {
            if (mapFlyingRef.current) return;

            handleOnBoundsChanged();
        });

        map.addListener("idle", () => {
            mapFlyingRef.current = false;
        });
    };

    const updateStoresAround = async (storeId: string, tags: Array<string>) => {
        if (!isDefined(StoresService)) return;

        const { stores, query } = await findStoresByStore(
            StoresService,
            storeUrls,
            storeId,
            tags
        );
        const centerStore = stores.at(0);
        if (!centerStore) return;

        setMapQuery(query);
        setMapSelection(centerStore);

        flyTo({
            center: {
                lat: centerStore.geometry.coordinates[1],
                lng: centerStore.geometry.coordinates[0]
            },
            zoom: LOC_ZOOM
        });

        setSearchItem(storeFeatureMapper(centerStore));
        setStores(stores);
    };

    const updateStoresByCoordinates = async (
        latLng: woosmap.map.LatLngLiteral,
        tags: Array<string>
    ) => {
        if (
            !isDefined(LocalitiesService) ||
            !isDefined(StoresService) ||
            !isDefined(DistanceService)
        ) {
            return;
        }

        findGeocode(LocalitiesService, latLng).then((geocodeResult) => {
            if (!isDefined(geocodeResult)) return;

            const searchItem = geocodeMapper(geocodeResult);
            setSearchItem(searchItem);

            if (isDefined(geocodeResult.geometry?.location)) {
                flyTo({
                    center: geocodeResult.geometry.location,
                    zoom: LOC_ZOOM
                });
            }
        });

        findStoresByLatLng(
            StoresService,
            DistanceService,
            storeUrls,
            tags,
            latLng
        ).then(({ stores, query }) => {
            setMapQuery(query);
            setStores(stores);
        });
    };

    const updateStoresByMapBounds = async (
        tags: Array<string>,
        bounds: woosmap.map.LatLngBounds
    ) => {
        if (!isDefined(StoresService)) return;

        const { stores, query } = await findStoresByBounds(
            StoresService,
            storeUrls,
            tags,
            bounds
        );

        setMapQuery(query);
        resetSearchItem();
        setStores(stores);
    };

    const updateStoresByTags = async (tags: Array<string>) => {
        if (!isDefined(StoresService)) return;

        const { stores, query } = await findStoresByTags(
            StoresService,
            storeUrls,
            tags
        );

        setMapQuery(query);
        setStores(stores);
    };

    const handleUrl = async () => {
        const urlSearchParams = new URLSearchParams(location.search);

        const query = urlSearchParams.get("query");
        if (query) {
            urlSearchParams.delete("query");
            history.pushState(null, "", `?${urlSearchParams}`);

            return setSearchItem({
                value: query,
                label: query,
                type: SearchItemType.Query
            });
        }

        const storeId = urlSearchParams.get("storeId");
        const tags = urlSearchParams.getAll("features");

        const latitude = parseSearchParamAsFloat("latitude", urlSearchParams);
        const longitude = parseSearchParamAsFloat("longitude", urlSearchParams);

        if (storeId) {
            return updateStoresAround(storeId, tags);
        }

        clearMapSelection();

        if (isDefined(latitude) && isDefined(longitude)) {
            return updateStoresByCoordinates(
                {
                    lat: latitude,
                    lng: longitude
                },
                tags
            );
        }

        return updateStoresByTags(tags);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!isDefined(map)) return;

        initEvents();
        handleUrl();

        window.addEventListener("popstate", handleUrl);

        return function cleanUp() {
            map.unbindAll();
            window.removeEventListener("popstate", handleUrl);
        };
    }, [map]);

    const displayStores = isDefined(stores)
        ? isDefined(selectedStore)
            ? [
                  selectedStore,
                  ...stores.filter(
                      (store) =>
                          store.properties.user_properties.storeId !==
                          selectedStore.properties.user_properties.storeId
                  )
              ]
            : stores
        : [];

    return {
        selectedStore: selectedStore,
        searchItem: searchItem,
        stores: displayStores,
        storesLoading: !isDefined(stores),
        handleUrl: handleUrl,
        resetSearchItem: resetSearchItem
    };
};

export { useWoosmap };
